// ReportCard.tsx
import React, { useState, useMemo, useEffect } from 'react';
import Display from './components/Display';
import CategoriesList from './components/CategoriesList';
import CategoryDetails from './components/CategoryDetails';
import HowToImprove from './components/HowToImprove';
import { ReportCardProps, Category, TabType } from './types';
import { processCategoryData } from './utils';
import './styles.scss';
import Overlay from '../../components/Overlay';
import Modal from '../../components/Modal';
import { usePortals } from '../CreativeAnalytics/hooks/usePortals';
import usePortal from 'react-cool-portal';
import { useIterationsApi } from '../CreativeAnalytics/api';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from '../../components/Helpers/Error';
import { BasicLoading } from '../../components/Helpers/Loading';

const ReportCard: React.FC<ReportCardProps> = ({
  visual,
  categoryPerformanceData,
  loading,
  onClose,
  iterationsData = [],
  start_date,
  end_date,
}) => {
  const account_id = useSelector(({ ViewSettings: { account_id } }) => ({
    account_id,
  }));
  const iterationsApi = useIterationsApi();
  const [activeTab, setActiveTab] = useState<TabType>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const ReportCardPortal = usePortal({
    containerId: 'report-card-root-standalone',
    clickOutsideToHide: false,
  });

  const categories = useMemo(() => {
    return processCategoryData(categoryPerformanceData, visual.all_labels);
  }, [categoryPerformanceData, visual.all_labels]);

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
  };

  const handleBackFromDetails = () => {
    setSelectedCategory(null);
  };

  useEffect(() => {
    iterationsApi.get.request(account_id);
  }, []);

  const assetIterations = iterationsData.filter(
    (x) => x.source_asset_id === visual.id
  );

  return (
    <ReportCardPortal.Portal>
      <Overlay close={onClose}>
        <Modal styles={{ minWidth: '90vw' }} close={onClose}>
          <ErrorBoundary>
            <div className="report-card-container">
              <div className="report-card-tabs">
                <button
                  className={`tab ${
                    activeTab === 'categories' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('categories')}
                >
                  Report Card
                </button>
                <button
                  className={`tab ${
                    activeTab === 'howToImprove' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('howToImprove')}
                >
                  How to Improve
                  <span className="beta">BETA</span>
                </button>
              </div>

              <div className="report-card-content">
                <div className="display-column">
                  <Display visual={visual} />
                </div>

                <div className="content-column">
                  {loading ? (
                    <BasicLoading />
                  ) : !categories?.length ? (
                    <p>No data available.</p>
                  ) :(
                    activeTab === 'categories' && (
                      <>
                        {!selectedCategory ? (
                          <CategoriesList
                            categories={categories}
                            onCategoryClick={handleCategoryClick}
                          />
                        ) : (
                          <CategoryDetails
                            category={
                              categories.find(
                                (c) => c.name === selectedCategory
                              )!
                            }
                            onBack={handleBackFromDetails}
                          />
                        )}
                      </>
                    )
                  )}

                  {activeTab === 'howToImprove' && (
                    <HowToImprove
                      visual={visual}
                      iterationsData={iterationsData}
                      {...{
                        activeTab,
                        start_date,
                        end_date,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </ErrorBoundary>
        </Modal>
      </Overlay>
    </ReportCardPortal.Portal>
  );
};

export default ReportCard;
